/* eslint-disable react/no-danger */
import { Builder } from '@builder.io/react';
import classNames from 'classnames';
import { FC } from 'react';

import { Button } from '@catawiki/cata-style';

import { getBuilderIOComponentClassName } from '@/shared/utils/getBuilderIOComponentClassName';

import {
  BACKGROUND_COLOR_NAMES,
  BackgroundClassMap,
  COLOR_NAME_LIGHT_GRAY,
} from '../../../constants/colors';
import { cleanBuilderStringInput } from '../../../text';
import { Media } from '../../Media';

export enum MediaPosition {
  LEFT = 'left',
  RIGHT = 'right',
}
export enum MediaType {
  NONE = 'none',
  VIDEO = 'video',
  IMAGE = 'image',
}
export interface Media {
  image?: string;
  video?: string;
  position: MediaPosition;
}

interface BuilderInput {
  title: string;
  content: string;
  buttonLabel: string;
  articleUrl: string;
  backgroundColorClassName: string;
  image?: string;
  imagePosition?: MediaPosition;
  mediaType: MediaType;
  media: Media;
  showButton: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: any;
}

type Props = BuilderInput & {
  locale: string;
};

const LeftMedia: FC<{ locale: string; type: MediaType; media?: Media }> = ({
  locale,
  type,
  media,
}) => {
  if (!media) return null;
  if (media.position !== MediaPosition.LEFT) return null;
  return (
    <Media
      locale={locale}
      mediaType={type}
      image={media.image}
      video={{
        source: media.video || '',
        poster: media.image,
        autoPlay: false,
      }}
    />
  );
};

const RightMedia: FC<{ locale: string; type: MediaType; media?: Media }> = ({
  locale,
  type,
  media,
}) => {
  if (!media) return null;
  if (media.position !== MediaPosition.RIGHT) return null;
  return (
    <Media
      locale={locale}
      mediaType={type}
      image={media.image}
      video={{
        source: media.video || '',
        poster: media.image,
        autoPlay: false,
      }}
    />
  );
};

const ArticleButton: FC<Props> = ({
  showButton = true,
  buttonLabel = 'Discover more',
  articleUrl = '#',
}) => {
  if (!showButton) return null;
  return (
    <div>
      <Button
        className="p-article-button"
        as="a"
        href={cleanBuilderStringInput(articleUrl, true)}
      >
        {cleanBuilderStringInput(buttonLabel, true)}
      </Button>
    </div>
  );
};

// TODO: Remove this after all images are gone
export function getMedia(
  type: MediaType,
  media?: Media,
  image?: string,
  imagePosition?: MediaPosition
): Media | undefined {
  if (type !== MediaType.NONE) return media;
  if (image) {
    return {
      image,
      position: imagePosition || MediaPosition.LEFT,
    };
  }
  return undefined;
}

export const Article: FC<Props> = (props) => {
  const {
    title = 'Lorem ipsum <span class="u-typography-h1-serif u-color-brand">blog article</span>',
    content = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod ' +
      'tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim ' +
      'veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea ' +
      'commodo consequat.',
    backgroundColorClassName = 'u-bgcolor-light-gray',
    // TODO: Remove these image keys when all pages move to media key
    image,
    imagePosition,
    mediaType = MediaType.NONE,
    media,
    attributes,
    locale,
  } = props;
  const mediaWithDeprecatedImage = getMedia(
    mediaType,
    media,
    image,
    imagePosition
  );
  const typeWithDeprecatedImage = (() => {
    if (mediaType !== MediaType.NONE) {
      return mediaType;
    } else if (mediaWithDeprecatedImage !== undefined) {
      return MediaType.IMAGE;
    } else {
      return MediaType.NONE;
    }
  })();
  const styles = getBuilderIOComponentClassName(attributes);
  const spacing =
    styles ||
    classNames(
      'u-p-t-xxl-14',
      'u-p-b-xxl-14',
      'u-p-t-sm-xxl-4',
      'u-p-b-sm-xxl-4',
      attributes?.className
    );
  return (
    <section {...attributes} className={`${backgroundColorClassName}`}>
      <div className="u-wrapper">
        <div className={classNames('p-article', 'u-flex', spacing)}>
          <LeftMedia
            locale={locale}
            type={typeWithDeprecatedImage}
            media={mediaWithDeprecatedImage}
          />
          <div className="p-article-text-container u-flex u-flex--direction-column u-flex--justify-content-center">
            <h2
              className="u-typography-h2 u-m-b-l u-m-t-sm-m u-m-t-md-m"
              dangerouslySetInnerHTML={{
                __html: cleanBuilderStringInput(title),
              }}
            />
            <div
              className="u-typography-h6 u-m-b-l"
              dangerouslySetInnerHTML={{ __html: content }}
            />
            <ArticleButton {...props} />
          </div>
          <RightMedia
            locale={locale}
            type={typeWithDeprecatedImage}
            media={mediaWithDeprecatedImage}
          />
        </div>
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export function componentRegister() {
  const name = 'Article';
  Builder.registerComponent(Article, {
    name,
    inputs: [
      {
        name: 'title',
        friendlyName: 'Title',
        type: 'string',
      },
      {
        name: 'content',
        friendlyName: 'Content',
        type: 'string',
      },
      {
        name: 'buttonLabel',
        friendlyName: 'Button Label',
        type: 'string',
      },
      {
        name: 'articleUrl',
        friendlyName: 'Article URL',
        type: 'string',
      },
      {
        name: 'backgroundColorClassName',
        friendlyName: 'Background Color',
        type: 'string',
        defaultValue: BackgroundClassMap[COLOR_NAME_LIGHT_GRAY],
        enum: BACKGROUND_COLOR_NAMES.map((value) => ({
          label: value,
          value: BackgroundClassMap[value],
        })),
      },
      {
        name: 'mediaType',
        friendlyName: 'Article Media Type',
        type: 'string',
        defaultValue: 'none',
        required: true,
        enum: Object.values(MediaType).map((value) => ({
          label: value,
          value,
        })),
      },
      {
        name: 'media',
        friendlyName: 'Article Media',
        type: 'object',
        defaultValue: {
          source: '',
          position: 'left',
        },
        showIf: `options.get('mediaType') !== 'none'`,
        subFields: [
          {
            name: 'image',
            friendlyName: 'Image or Video Poster Image',
            type: 'file',
            allowedFileTypes: ['jpeg', 'png'],
          },
          {
            name: 'video',
            friendlyName: 'Video URL or Youtube URL',
            type: 'file',
            allowedFileTypes: ['mp4'],
          },
          {
            name: 'position',
            friendlyName: 'Media Position',
            type: 'string',
            defaultValue: 'left',
            enum: Object.values(MediaPosition).map((value) => ({
              label: value,
              value,
            })),
          },
        ],
      },
      {
        name: 'showButton',
        friendlyName: 'Show button',
        type: 'boolean',
        defaultValue: true,
      },
    ],
    hideFromInsertMenu: true,
    noWrap: true,
  });
  return name;
}
